import React from 'react';
import { Alert, Button, Flex } from '@itandi/itandi-bb-ui';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import { PATH } from '~/pages/deals/[id]';
import { PATH as VENDOR_PATH } from '~/pages/vendors/deals/[id]';
import { Deal } from '~/models/Deal';
import Styles from './ConstructionFooter.module.scss';
import { IN_CONSTRUCTION, isOwnStatus, SEND_ESTIMATE } from '~/models/Status';
import { isSuccess } from '~/utils/api/api_handler';
import { NskResponse } from '~/models/Api';
import {
  DealEstimationHeader,
  estimationRequestSide
} from '~/models/DealEstimationHeader';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { ConfirmModal } from '../parts/modal/ConfirmModal';
import { ItandiAccount } from '~/models/ItandiAccount';
import { DealEstimationContext } from '~/models/DealEstimate';

type Props = {
  deal: Deal;
  isAdmin: boolean;
  companyType: 'client' | 'vendor' | 'partner';
  dealEstimationHeader: DealEstimationHeader | null;
  handleUpdateAcceptStatus: (
    dealId: number,
    dealEstimationHeaderId: number
  ) => Promise<NskResponse<void>>;
  handleUpdateRejectStatus: (
    dealId: number,
    dealEstimationHeaderId: number,
    status: 'reject'
  ) => Promise<NskResponse<void>>;
  handleMutate: () => Promise<void>;
  handleAcceptForward?:
    | ((dealId: number, statusId: number) => Promise<NskResponse<void>>)
    | null;
  dealEstimationContext?: DealEstimationContext | null;
  account?: ItandiAccount | null;
};

export const ConstructionConfirmedFooter: React.FC<Props> = ({
  deal,
  isAdmin,
  companyType,
  dealEstimationHeader,
  handleUpdateAcceptStatus,
  handleUpdateRejectStatus,
  handleMutate,
  handleAcceptForward = null,
  dealEstimationContext = null,
  account = null
}: Props) => {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { isOpen, handleOpen, handleClose } = useOpenHandler();
  const {
    isOpen: isOpenUpdateStatus,
    handleOpen: handleOpenUpdateStatus,
    handleClose: handleCloseUpdateStatus
  } = useOpenHandler();
  const {
    isOpen: isOpenAccept,
    handleOpen: handleOpenAccept,
    handleClose: handleCloseAccept
  } = useOpenHandler();
  const {
    isOpen: isOpenReject,
    handleOpen: handleOpenReject,
    handleClose: handleCloseReject
  } = useOpenHandler();
  const onClickUpdateStatus = async (): Promise<void> => {
    if (handleAcceptForward === null) return;
    setIsSubmitting(true);
    const result = await handleAcceptForward(deal.id, IN_CONSTRUCTION);
    if (isSuccess(result)) {
      toast.success('ステータスを進めました');
      await router.push(
        companyType === 'client' ? PATH(deal.id) : VENDOR_PATH(deal.id)
      );
      setIsSubmitting(false);
      return;
    }
    handleCloseUpdateStatus();
    setIsSubmitting(false);
    toast.error(result.message ?? 'エラーが発生しました。');
  };
  // 申請中の見積りがない場合は見積り確認画面でステータス進行のみできるようにする
  if (
    dealEstimationHeader === null &&
    deal.status === SEND_ESTIMATE &&
    dealEstimationContext &&
    account
  ) {
    return (
      <div className={Styles.ButtonArea}>
        <Flex
          gap="8px"
          justifyContent="right"
          marginTop="16px"
          padding="16px 8px"
        >
          <Button
            disabled={
              !account.staff?.staffAuthorization.enableStatus ||
              dealEstimationContext.dealConstructions.length === 0
            }
            onClick={handleOpenUpdateStatus}
            variant="primary"
          >
            ステータスを進める
          </Button>
          <ConfirmModal
            confirmType="save"
            handleAccept={onClickUpdateStatus}
            handleCancel={handleCloseUpdateStatus}
            isOpen={isOpenUpdateStatus}
          >
            ステータスを進めてよろしいですか？
            <Alert marginTop=".5rem" variant="info">
              確定ボタンを押すと工事の手配へ移ります。
            </Alert>
          </ConfirmModal>
        </Flex>
      </div>
    );
  }
  if (
    // 非共有案件で承認等の操作は行わない
    isOwnStatus(deal.status) ||
    // 実施会社は承認等の操作は行わない
    companyType === 'partner' ||
    dealEstimationHeader === null ||
    estimationRequestSide(deal, companyType)
  ) {
    return null;
  }
  // 工事完了待ちの時の反映（承認）は重い操作
  const lightConfirmed =
    companyType === 'client' && deal.status === SEND_ESTIMATE;
  const onClickAccept = async (): Promise<void> => {
    setIsSubmitting(true);
    const result = await handleUpdateAcceptStatus(
      deal.id,
      dealEstimationHeader.id
    );
    if (isSuccess(result)) {
      handleMutate();
      handleCloseAccept();
      setIsSubmitting(false);
      toast.success('申請を承認しました');
      return;
    }
    handleCloseAccept();
    setIsSubmitting(false);
    toast.error(result.message ?? 'エラーが発生しました。');
  };
  const onClickReject = async (): Promise<void> => {
    setIsSubmitting(true);
    const result = await handleUpdateRejectStatus(
      deal.id,
      dealEstimationHeader.id,
      'reject'
    );
    if (isSuccess(result)) {
      handleMutate();
      toast.success('申請を棄却しました');
      handleCloseReject();
      setIsSubmitting(false);
      return;
    }
    handleCloseReject();
    setIsSubmitting(false);
    toast.error(result.message ?? 'エラーが発生しました。');
  };
  const onClickSubmit = async (): Promise<void> => {
    if (handleAcceptForward === null) return;
    setIsSubmitting(true);
    const response = await handleUpdateAcceptStatus(
      deal.id,
      dealEstimationHeader.id
    );
    if (isSuccess(response)) {
      const result = await handleAcceptForward(deal.id, IN_CONSTRUCTION);
      if (isSuccess(result)) {
        handleMutate();
        toast.success('申請を承認してステータスを進めました');
      }
      await router.push(
        companyType === 'client' ? PATH(deal.id) : VENDOR_PATH(deal.id)
      );
      handleClose();
      setIsSubmitting(false);
      return;
    }
    handleClose();
    setIsSubmitting(false);
    toast.error(response.message ?? 'エラーが発生しました。');
  };
  return (
    <div className={Styles.ButtonArea}>
      <Flex
        gap="8px"
        justifyContent="right"
        marginTop="16px"
        padding="16px 8px"
      >
        <Button
          disabled={isAdmin || isSubmitting}
          onClick={handleOpenReject}
          variant="tertiary"
        >
          申請を棄却する
        </Button>
        <Button
          disabled={isAdmin || isSubmitting}
          onClick={handleOpenAccept}
          variant={lightConfirmed ? 'secondary' : 'primary'}
        >
          {lightConfirmed ? '申請を反映する' : '申請を承認する'}
        </Button>
        {account &&
          account.staff?.staffAuthorization.enableStatus &&
          deal.status === SEND_ESTIMATE && (
            <Button
              disabled={isAdmin || isSubmitting}
              onClick={handleOpen}
              variant="primary"
            >
              反映してステータスを進める
            </Button>
          )}
      </Flex>
      <ConfirmModal
        confirmType="save"
        handleAccept={onClickReject}
        handleCancel={handleCloseReject}
        isOpen={isOpenReject}
      >
        見積りの申請を棄却してよろしいですか？
        <Alert marginTop=".5rem" variant="warning">
          申請された情報が棄却されます。内容は反映されません。
        </Alert>
      </ConfirmModal>
      <ConfirmModal
        confirmType="save"
        handleAccept={onClickAccept}
        handleCancel={handleCloseAccept}
        isOpen={isOpenAccept}
      >
        {lightConfirmed
          ? '見積りの申請を反映してよろしいですか？'
          : '見積りの申請を承認してよろしいですか？'}
        <Alert marginTop=".5rem" variant="warning">
          {lightConfirmed
            ? '申請された情報が反映されます。工事完了待ちに進めるまで発注扱いとはなりません。'
            : '工事完了待ちステータスの場合、見積りの内容が施工対象となります。'}
        </Alert>
      </ConfirmModal>
      <ConfirmModal
        confirmType="save"
        handleAccept={onClickSubmit}
        handleCancel={handleClose}
        isOpen={isOpen}
      >
        見積りの申請を反映し、ステータスを進めますか？
        <Alert marginTop=".5rem" variant="warning">
          申請された情報が反映され、工事完了待ちへステータスが変更されます。
          <br />
          確定ボタンを押すと工事の手配へ移ります。
        </Alert>
      </ConfirmModal>
    </div>
  );
};
